import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { BarrioContext } from '../components/BarrioContext';
import LoadingOverlay from '../components/LoadingOverlay';
import SummaryStatsCard, { SUMMARY_STATS_THEMES } from '../components/SummaryStatsCard';
import { getResumenExpensas } from '../service/DashboardService';

const firstOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const fmtImporte = (v) => v != null
    ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(v)
    : '—';

// Icono y color de cada sección conocida (definidas por el SP spGetResumenExpensasBarrio).
// Si el maestro de conceptos define una sección nueva, se le asigna un tema por defecto.
const SECCION_CONFIG = {
    'Saldo Inicial':                { icon: 'pi pi-flag',       theme: 'blue' },
    'Movimientos del mes':          { icon: 'pi pi-sync',       theme: 'green' },
    'Composición del Saldo Final':  { icon: 'pi pi-briefcase',  theme: 'purple' },
};
const SECCION_DEFAULT = { icon: 'pi pi-tags', theme: 'blue' };

const EstadoFinancieroResumen = () => {
    const { barrio } = useContext(BarrioContext);
    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');

    const [periodo, setPeriodo] = useState(firstOfMonth(new Date()));
    const [resumen, setResumen] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchResumen = useCallback(async () => {
        if (!barrio?.id || !periodo) return;
        setLoading(true);
        try {
            const { data } = await getResumenExpensas(barrio.id, administracionId, firstOfMonth(periodo));
            setResumen(data);
        } catch {
            setResumen([]);
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId, periodo]);

    useEffect(() => { fetchResumen(); }, [fetchResumen]);

    // El "Saldo Final" es un único total (no una lista de conceptos): se muestra
    // aparte, como una card destacada, en vez de agrupado con el resto de secciones.
    const saldoFinal = resumen.find(r => r.Seccion === 'Saldo Final');

    const secciones = [];
    resumen.forEach(r => {
        if (r.Seccion === 'Saldo Final') return;
        let grupo = secciones.find(s => s.nombre === r.Seccion);
        if (!grupo) {
            grupo = { nombre: r.Seccion, items: [] };
            secciones.push(grupo);
        }
        grupo.items.push(r);
    });

    return (
        <>
            <h4>Resumen</h4>
            <br />

            <LoadingOverlay visible={loading} />

            <div className="flex flex-column align-items-start gap-2 mb-3">
                <div>
                    <label style={lbl}>Período</label>
                    <Calendar value={periodo} onChange={(e) => setPeriodo(e.value)}
                        view="month" dateFormat="mm/yy" showButtonBar
                        className="p-inputtext-sm" style={{ width: 140 }} />
                </div>
            </div>

            {!loading && secciones.length === 0 && !saldoFinal && (
                <p style={{ color: '#667085' }}>No hay datos para este período.</p>
            )}

            <div className="grid">
                {secciones.map((seccion) => {
                    const cfg = SECCION_CONFIG[seccion.nombre] || SECCION_DEFAULT;
                    const t = SUMMARY_STATS_THEMES[cfg.theme];
                    const subtotal = seccion.items.reduce((sum, it) => sum + (it.Importe || 0), 0);

                    return (
                        <div className="col-12 md:col-6" key={seccion.nombre}>
                            <SummaryStatsCard icon={cfg.icon} title={seccion.nombre} theme={cfg.theme}>
                                <div style={{ marginTop: '0.9rem', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                                    {seccion.items.map((it, idx) => (
                                        <div className="flex justify-content-between align-items-center" key={idx}>
                                            <span style={{ fontSize: '0.85rem', color: '#475467' }}>{it.Concepto}</span>
                                            <span style={{ fontWeight: 600, fontSize: '0.9rem', color: t.value }}>{fmtImporte(it.Importe)}</span>
                                        </div>
                                    ))}
                                    <div className="flex justify-content-between align-items-center"
                                        style={{ borderTop: `1px solid ${t.divider}`, marginTop: '0.3rem', paddingTop: '0.6rem' }}>
                                        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: t.title }}>Total {seccion.nombre}</span>
                                        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: t.value }}>{fmtImporte(subtotal)}</span>
                                    </div>
                                </div>
                            </SummaryStatsCard>
                        </div>
                    );
                })}

                {saldoFinal && (
                    <div className="col-12">
                        <SummaryStatsCard icon="pi pi-check-circle" title="Saldo Final" theme="amber"
                            items={[{ label: 'Total del período', value: fmtImporte(saldoFinal.Importe) }]} />
                    </div>
                )}
            </div>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default EstadoFinancieroResumen;
